import React from 'react';

/**
 * Responsive YouTube embed in the Talas dark-gold theme.
 *
 *   <YouTubeCard
 *     videoId="zQIYYFIqOq4"
 *     title="Talas AI Introduction"
 *     params={{ autoplay: 1, mute: 1 }}
 *     header={{ eyebrow: 'PRODUCT DEMO', title: 'See Talas AI in action' }}
 *   />
 *
 * - 16:9 aspect ratio via padding-bottom (no JS).
 * - Gold border + ambient glow that matches BetaBanner / GoldenButton styling.
 * - `params` are merged into the embed URL query. Sensible defaults:
 *     controls=1, rel=0, playsinline=1
 * - When `header` is provided, renders a small caption strip above the player.
 */
export default function YouTubeCard({
  videoId,
  title = 'Video',
  params = {},
  header = null,
  testid = 'youtube-card',
  className = '',
}) {
  const mergedParams = {
    controls: 1,
    rel: 0,
    playsinline: 1,
    ...params,
  };
  const search = Object.entries(mergedParams)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const src = `https://www.youtube.com/embed/${videoId}?${search}`;

  return (
    <div
      data-testid={testid}
      className={`relative group ${className}`}
    >
      {/* Ambient gold glow — soft halo behind the card */}
      <div className="absolute -inset-px rounded-md bg-gradient-to-b from-[#d8b34a]/35 via-[#d8b34a]/10 to-transparent pointer-events-none opacity-90 group-hover:opacity-100 transition-opacity" />
      <div className="absolute -inset-3 rounded-md bg-[#d8b34a]/[0.06] blur-2xl pointer-events-none" aria-hidden="true" />

      <div className="relative bg-[#0c0c0e] border border-white/10 rounded-md overflow-hidden shadow-[0_30px_80px_-30px_rgba(216,179,74,0.25)]">
        {header && (
          <div className="px-4 sm:px-5 py-3.5 border-b border-white/5 flex items-center gap-3">
            {header.eyebrow && (
              <span className="text-[10px] tracking-[0.3em] font-mono gold-text whitespace-nowrap">
                {header.eyebrow}
              </span>
            )}
            {header.eyebrow && header.title && <span className="h-3 w-px bg-white/10" />}
            {header.title && (
              <p className="text-sm font-semibold text-white truncate">{header.title}</p>
            )}
          </div>
        )}

        {/* 16:9 responsive frame */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={src}
            title={title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid={`${testid}-iframe`}
          />
        </div>

        {header && header.subtitle && (
          <div className="px-4 sm:px-5 py-3 border-t border-white/5">
            <p className="text-xs text-zinc-400 leading-relaxed">{header.subtitle}</p>
          </div>
        )}
      </div>
    </div>
  );
}
